// MiniApp webviews (Max Messenger / Telegram) may run in a sandboxed iframe where
// window.localStorage / window.sessionStorage access throws a SecurityError.
// All storage reads in routing/layout guards must go through these safe wrappers.

const STORAGE_KEYS = {
  accessToken: "access_token",
  refreshToken: "refresh_token",
} as const;

const safeGet = (key: string): string | null => {
  try {
    return localStorage.getItem(key) ?? sessionStorage.getItem(key);
  } catch {
    return null;
  }
};

/** Decodes the exp claim (epoch seconds) from a JWT without verifying the signature. */
const getJwtExpiration = (token: string): number | null => {
  try {
    const payload = token.split(".")[1];
    if (!payload) return null;
    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const json = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + c.charCodeAt(0).toString(16).padStart(2, "0"))
        .join(""),
    );
    const parsed = JSON.parse(json) as { exp?: number };
    return typeof parsed.exp === "number" ? parsed.exp : null;
  } catch {
    return null;
  }
};

/**
 * Returns true when a usable auth token is present in localStorage/sessionStorage.
 * An expired access token is only considered usable when a refresh token exists
 * (the session can then be restored via POST /api/auth/refresh).
 */
export const hasStoredTokens = (): boolean => {
  const accessToken = safeGet(STORAGE_KEYS.accessToken);
  const refreshToken = safeGet(STORAGE_KEYS.refreshToken);

  if (!accessToken && !refreshToken) return false;
  if (!accessToken) return !!refreshToken;

  const exp = getJwtExpiration(accessToken);
  if (exp === null) return true; // cannot decode — assume usable

  const currentEpochSeconds = Math.floor(Date.now() / 1000);
  if (exp > currentEpochSeconds) return true; // access token still fresh

  // Access token expired — session can only be restored via refresh_token.
  return !!refreshToken;
};
